import React, { useContext, useState, useCallback } from "react";
import { toast } from "sonner";
import { DataService } from "@/services/data";
import { type ConversationDetail } from "@/types/crm/sms";

type OpenFromPhoneNumberOptions = {
    object_type?: string | null;
    object_id?: string | number | null;
};

type SmsContextType = {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    focusedConversationId: number | null;
    setFocusedConversationId: React.Dispatch<React.SetStateAction<number | null>>;
    prefetchedConversation: ConversationDetail | null;
    setPrefetchedConversation: React.Dispatch<React.SetStateAction<ConversationDetail | null>>;
    openFromPhoneNumber: (phone_number: string, options?: OpenFromPhoneNumberOptions) => Promise<ConversationDetail>;
};

const SmsContext = React.createContext<SmsContextType>(null!);

export function SmsProvider({ children }: { children: React.ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedConversationId, setFocusedConversationId] = useState<number | null>(null);
    const [prefetchedConversation, setPrefetchedConversation] = useState<ConversationDetail | null>(null);

    const openFromPhoneNumber = useCallback(async (phone_number: string, options?: OpenFromPhoneNumberOptions) => {
        try {
            const conversation = await DataService.client
                .url("/crm/app/conversations/open_from_object/")
                .post({
                    phone_number,
                    object_type: options?.object_type ?? undefined,
                    object_id: options?.object_id ?? undefined,
                })
                .json<ConversationDetail>();
            setPrefetchedConversation(conversation);
            setFocusedConversationId(conversation.id);
            setIsOpen(true);
            return conversation;
        } catch (error: any) {
            const message = error?.json?.error || error?.message || "Could not open the SMS conversation.";
            toast.error(message);
            throw error;
        }
    }, []);

    const value = { isOpen, setIsOpen, focusedConversationId, setFocusedConversationId, prefetchedConversation, setPrefetchedConversation, openFromPhoneNumber };
    return <SmsContext.Provider value={value}>{children}</SmsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSms() {
    return useContext(SmsContext);
}
